/**
 * Componente InnerButton - Versión refactorizada
 *
 * Este componente ha sido reestructurado siguiendo Atomic Design:
 * - Utilidades extraídas a utils/
 * - Estilos extraídos a styles/
 * - Lógica de estado en hooks personalizados
 * - Componentes atómicos para cada parte del botón
 *
 * Esto hace que el código sea más mantenible, testeable y escalable.
 */

import React from 'react';
import { cn } from '@/shared/utils/cn';
import type {
  InnerLegacyButtonProps,
  LegacyButtonIconConfig,
  LegacyButtonSlotConfig,
  LegacyVisualSlotType,
} from '@/shared/types/legacyButton';
import type { LegacyIconName } from '@/shared/types/legacyIcon';
import { isLikelyUrl } from './utils/legacySlotUrl';
import { ButtonTooltipConfig } from '@/shared/types/buttonConfig';
import { Tooltip } from '@/modules/Tooltip';
import { useBadgeLoading, useHoverText, useButtonClasses } from '@/modules/InnerButton/hooks';
import {
  shouldShowBadge,
  shouldBadgeHaveStroke,
  getEffectiveBadgeSize,
} from '@/modules/InnerButton/utils/badgeUtils';
import { ButtonContent } from '@/modules/InnerButton/components/ButtonContent';
import {
  LegacyButtonIcon,
  LegacyButtonSlotLeft,
  LegacyButtonSlotRight,
  LegacyButtonBadge,
} from './components';
import {
  convertLegacyIconToLegacyIconConfig,
  convertLegacyBadgeToLegacyBadgeConfig,
  convertLegacyLeftSlotToLegacySlotConfig,
  convertLegacyRightSlotToLegacySlotConfig,
  convertLegacyTooltipToTooltipConfig,
} from './utils/legacyConfigConverters';

/**
 * Valida props en desarrollo
 */
const validateProps = (props: Partial<InnerLegacyButtonProps>) => {
  if (process.env.NODE_ENV !== 'production') {
    if (props.theme && !['solid', 'icon'].includes(props.theme)) {
      // eslint-disable-next-line no-console
      console.warn(`InnerLegacyButton: Invalid theme "${props.theme}", using "solid" as fallback`);
    }
    if (props.size && !['xxs', 'xs', 'sm', 'md', 'lg'].includes(props.size)) {
      // eslint-disable-next-line no-console
      console.warn(`InnerLegacyButton: Invalid size "${props.size}", using "md" as fallback`);
    }
    if (
      props.color &&
      !['primary', 'secondary', 'tertiary', 'destructive', 'link', 'ghost', 'soft'].includes(
        props.color,
      )
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `InnerLegacyButton: Invalid color "${props.color}", using "primary" as fallback`,
      );
    }
    if (props.pill && !['none', 'xs', 'sm', 'md', 'lg', 'pill'].includes(props.pill)) {
      // eslint-disable-next-line no-console
      console.warn(`InnerLegacyButton: Invalid pill "${props.pill}", using "sm" as fallback`);
    }
    if (props.as && !['button', 'div'].includes(props.as)) {
      // eslint-disable-next-line no-console
      console.warn(`InnerLegacyButton: Invalid as "${props.as}", using "button" as fallback`);
    }
    if (props.link && props.as === 'div') {
      // eslint-disable-next-line no-console
      console.warn('InnerLegacyButton: "link" prop is ignored when as="div"');
    }
  }
};

export const InnerLegacyButton = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement | HTMLDivElement,
  InnerLegacyButtonProps
>(function InnerLegacyButton(
  {
    className,
    classNameLabel,
    fullWidth = false,
    leftSlot,
    rightSlot,
    theme = 'solid',
    badgeColor,
    badgeLabel,
    badgeTheme = 'solid',
    badgeSize = 'sm',
    badgeStroke,
    badgeIcon,
    badgeIconDuotonePrimary,
    badgeIconDuotoneSecondary,
    badgeIconDuotoneOpacityPrimary,
    badgeIconDuotoneOpacitySecondary,
    badgeLoading,
    hoverText,
    color,
    rounded,
    children,
    textColor,
    clickable = true,
    icon,
    link,
    size,
    square = false,
    target = '_self',
    onClickIcon,
    onClickLeftSlot,
    onClickRightSlot,
    disabled,
    colorIcon,
    iconDuotonePrimary,
    iconDuotoneSecondary,
    iconDuotoneOpacityPrimary,
    iconDuotoneOpacitySecondary,
    leftSlotDuotonePrimary,
    leftSlotDuotoneSecondary,
    leftSlotDuotoneOpacityPrimary,
    leftSlotDuotoneOpacitySecondary,
    rightSlotDuotonePrimary,
    rightSlotDuotoneSecondary,
    rightSlotDuotoneOpacityPrimary,
    rightSlotDuotoneOpacitySecondary,
    showShadow = false,
    borderable,
    iconSizeExtended = 'none',
    defaultSizeIcon = true,
    as = 'button',
    classNameId = 'imj-ui-inner-button',
    tooltip = '',
    tooltipPosition,
    tooltipAlignment,
    tooltipDelay,
    tooltipAbsolute,
    classNameTooltip,
    stopPropagation = true,
    pointer = true,
    onClick,
    pill = 'sm',
    textAlign = 'center',
    whiteBackground = true,
    strokeWidth = 2,
    transition = true,
    classNameIcon = '',
    active = false,
    // Nuevo sistema de configuraciones
    iconProps: iconPropsProp,
    badge: badgeProp,
    leftSlotProps: leftSlotPropsProp,
    rightSlotProps: rightSlotPropsProp,
    tooltipProps: tooltipPropsProp,
    ...props
  },
  ref,
) {
  // Validar props en desarrollo
  validateProps({ theme, size, color, pill, as, link });

  // Props específicas de button
  const buttonProps = {
    disabled,
  };

  // Detectar si icon es un objeto de configuración (LegacyButtonIconConfig)
  const isIconConfigObject =
    icon &&
    typeof icon === 'object' &&
    !React.isValidElement(icon) &&
    'icon' in icon &&
    ('color' in icon ||
      'size' in icon ||
      'show' in icon ||
      'onClick' in icon ||
      'className' in icon ||
      'strokeWidth' in icon);

  const iconAsConfig = isIconConfigObject ? (icon as LegacyButtonIconConfig) : undefined;

  const legacyIconConfig = !isIconConfigObject
    ? convertLegacyIconToLegacyIconConfig({
      icon: icon as LegacyVisualSlotType | React.ReactElement | undefined,
      iconDuotonePrimary,
      iconDuotoneSecondary,
      iconDuotoneOpacityPrimary,
      iconDuotoneOpacitySecondary,
      colorIcon,
      classNameIcon,
      strokeWidth,
      onClickIcon,
    })
    : undefined;

  const legacyBadgeConfig = convertLegacyBadgeToLegacyBadgeConfig({
    badgeColor,
    badgeLabel,
    badgeTheme,
    badgeSize,
    badgeStroke,
    badgeIcon,
    badgeIconDuotonePrimary,
    badgeIconDuotoneSecondary,
    badgeIconDuotoneOpacityPrimary,
    badgeIconDuotoneOpacitySecondary,
    badgeLoading,
  });

  // Detectar si leftSlot es un objeto de configuración
  const isLeftSlotConfigObject =
    leftSlot &&
    typeof leftSlot === 'object' &&
    !React.isValidElement(leftSlot) &&
    'icon' in leftSlot &&
    ('colorIcon' in leftSlot ||
      'onClick' in leftSlot ||
      'show' in leftSlot ||
      'iconDuotonePrimary' in leftSlot);

  const leftSlotAsConfig = isLeftSlotConfigObject
    ? (leftSlot as LegacyButtonSlotConfig)
    : undefined;

  // Detectar si rightSlot es un objeto de configuración
  const isRightSlotConfigObject =
    rightSlot &&
    typeof rightSlot === 'object' &&
    !React.isValidElement(rightSlot) &&
    'icon' in rightSlot &&
    ('colorIcon' in rightSlot ||
      'onClick' in rightSlot ||
      'show' in rightSlot ||
      'iconDuotonePrimary' in rightSlot);

  const rightSlotAsConfig = isRightSlotConfigObject
    ? (rightSlot as LegacyButtonSlotConfig)
    : undefined;

  // Convertir props legacy a configuraciones si no se proporcionan las nuevas
  // Solo convertir si leftSlot/rightSlot NO son objetos de configuración
  const legacyLeftSlotConfig = !isLeftSlotConfigObject
    ? convertLegacyLeftSlotToLegacySlotConfig({
      leftSlot: leftSlot as LegacyVisualSlotType | undefined,
      leftSlotDuotonePrimary,
      leftSlotDuotoneSecondary,
      leftSlotDuotoneOpacityPrimary,
      leftSlotDuotoneOpacitySecondary,
      onClickLeftSlot,
      colorIcon,
    })
    : undefined;

  const legacyRightSlotConfig = !isRightSlotConfigObject
    ? convertLegacyRightSlotToLegacySlotConfig({
      rightSlot: rightSlot as LegacyVisualSlotType | undefined,
      rightSlotDuotonePrimary,
      rightSlotDuotoneSecondary,
      rightSlotDuotoneOpacityPrimary,
      rightSlotDuotoneOpacitySecondary,
      onClickRightSlot,
      colorIcon,
    })
    : undefined;

  // Detectar si tooltip es un objeto de configuración
  const isTooltipConfigObject =
    tooltip &&
    typeof tooltip === 'object' &&
    ('label' in tooltip ||
      'position' in tooltip ||
      'alignment' in tooltip ||
      'delay' in tooltip ||
      'absolute' in tooltip ||
      'className' in tooltip);

  // Si tooltip es un objeto de configuración, usarlo directamente
  const tooltipAsConfig = isTooltipConfigObject ? (tooltip as ButtonTooltipConfig) : undefined;

  // Convertir props legacy a configuraciones si no se proporcionan las nuevas
  // Solo convertir si tooltip NO es un objeto de configuración
  const legacyTooltipConfig = !isTooltipConfigObject
    ? convertLegacyTooltipToTooltipConfig({
      tooltip: tooltip as string | undefined,
      tooltipPosition,
      tooltipAlignment,
      tooltipDelay,
      tooltipAbsolute,
      classNameTooltip,
    })
    : undefined;

  // Prioridad: iconProps > icon como objeto > configuraciones legacy > icon simple
  // Si hay icon pero no iconProps, crear un objeto de configuración mínimo
  const iconProps =
    iconPropsProp ||
    iconAsConfig ||
    legacyIconConfig ||
    (icon &&
    !isIconConfigObject &&
    !React.isValidElement(icon) &&
    typeof icon === 'string' &&
    !isLikelyUrl(icon)
      ? { icon: icon as LegacyIconName, show: true }
      : undefined);
  const badge = badgeProp || legacyBadgeConfig;
  // Prioridad: leftSlotProps > leftSlot como objeto > configuraciones legacy > leftSlot simple
  const leftSlotProps = leftSlotPropsProp || leftSlotAsConfig || legacyLeftSlotConfig;
  // Prioridad: rightSlotProps > rightSlot como objeto > configuraciones legacy > rightSlot simple
  const rightSlotProps = rightSlotPropsProp || rightSlotAsConfig || legacyRightSlotConfig;
  // Prioridad: tooltipProps > tooltip como objeto > configuraciones legacy > tooltip simple
  const tooltipConfig = tooltipPropsProp || tooltipAsConfig || legacyTooltipConfig;

  // Extraer valores de las configuraciones o usar props legacy como fallback
  // Si icon es un objeto de configuración, ya está en iconProps, así que usar undefined para evitar duplicación
  // icon puede ser VisualSlotType | React.ReactElement | ButtonIconConfig
  // pero ButtonIcon solo acepta IconType | AnyIconDefinition | React.ReactElement
  // Necesitamos filtrar solo los valores compatibles
  const effectiveIcon: LegacyVisualSlotType | React.ReactElement | undefined =
    iconProps?.icon ||
    (isIconConfigObject
      ? undefined
      : React.isValidElement(icon)
        ? icon
        : icon !== undefined && icon !== null
          ? (icon as LegacyVisualSlotType)
          : undefined);
  const effectiveBadgeColor = badge?.color || badgeColor;
  const effectiveBadgeLabel = badge?.label || badgeLabel;
  const effectiveBadgeTheme = badge?.theme || badgeTheme;
  const effectiveBadgeSize = badge?.size || badgeSize;
  const effectiveBadgeStroke = badge?.stroke ?? badgeStroke;
  // badge?.icon ya es del tipo correcto (IconType | AnyIconDefinition | undefined)
  // badgeIcon viene de InnerButtonProps y es IconType, que es compatible con IconType | AnyIconDefinition
  const effectiveBadgeIcon = badge?.icon || badgeIcon;
  const effectiveBadgeLoading = badge?.loading ?? badgeLoading;
  // Si leftSlot/rightSlot son objetos de configuración, ya están en leftSlotProps/rightSlotProps
  const effectiveLeftSlot =
    leftSlotProps?.icon ||
    (isLeftSlotConfigObject ? undefined : (leftSlot as LegacyVisualSlotType | undefined));
  const effectiveRightSlot =
    rightSlotProps?.icon ||
    (isRightSlotConfigObject ? undefined : (rightSlot as LegacyVisualSlotType | undefined));

  // Extraer valores del tooltip (configuración o legacy)
  const effectiveTooltipLabel =
    tooltipConfig?.label || (isTooltipConfigObject ? undefined : (tooltip as string | undefined));
  const effectiveTooltipPosition = tooltipConfig?.position || tooltipPosition;
  const effectiveTooltipAlignment = tooltipConfig?.alignment || tooltipAlignment;
  const effectiveTooltipDelay = tooltipConfig?.delay ?? tooltipDelay;
  const effectiveTooltipAbsolute = tooltipConfig?.absolute ?? tooltipAbsolute;
  const effectiveTooltipClassName = tooltipConfig?.className || classNameTooltip;

  const isIconVariant = theme === 'icon';
  const isIconButton =
    (children === null || children === undefined || children === false) && !!effectiveIcon;
  const hasChildren = children !== null && children !== undefined && children !== false;

  // Hooks personalizados
  const badgeFinishedLoading = useBadgeLoading(effectiveBadgeLoading);

  const {
    displayText,
    textStyle,
    measureRef,
    textRef,
    handleMouseEnter,
    handleMouseLeave,
    measureSpanClassName,
  } = useHoverText({
    children,
    hoverText,
    badgeFinishedLoading,
    size,
    textColor,
    textAlign,
  });

  // Lógica de badge
  const shouldShowBadgeValue = shouldShowBadge(
    effectiveBadgeColor,
    effectiveBadgeLabel,
    effectiveBadgeIcon,
    effectiveBadgeSize,
    effectiveBadgeLoading,
  );
  const effectiveBadgeSizeValue = getEffectiveBadgeSize(effectiveBadgeLoading, effectiveBadgeSize);
  const computedBadgeStroke = shouldBadgeHaveStroke(
    effectiveBadgeStroke,
    effectiveBadgeColor,
    color,
  );

  // Generar clases del botón
  const { buttonClasses } = useButtonClasses({
    theme,
    size,
    color,
    rounded,
    fullWidth,
    pill,
    clickable,
    borderable,
    whiteBackground,
    active,
    square,
    icon: !!effectiveIcon,
    children: hasChildren,
    shouldShowBadge: shouldShowBadgeValue,
    showShadow,
    pointer,
    transition,
    classNameId,
    className,
    isIconVariant,
    defaultSizeIcon,
  });

  // Handler de click
  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement | HTMLDivElement>,
  ) => {
    if (stopPropagation) {
      e.stopPropagation();
    }
    if (onClick) {
      onClick(e);
    }
  };

  // Renderizar contenido interno
  const renderContent = () => (
    <>
      {/* Slot izquierdo - antes del contenido */}
      {leftSlotProps?.show !== false && (
        <LegacyButtonSlotLeft
          leftSlot={effectiveLeftSlot}
          size={size}
          color={color}
          colorIcon={leftSlotProps?.colorIcon || colorIcon}
          className={className}
          disabled={disabled}
          strokeWidth={strokeWidth}
          onClickLeftSlot={leftSlotProps?.onClick || onClickLeftSlot}
          leftSlotDuotonePrimary={leftSlotProps?.iconDuotonePrimary || leftSlotDuotonePrimary}
          leftSlotDuotoneSecondary={leftSlotProps?.iconDuotoneSecondary || leftSlotDuotoneSecondary}
          leftSlotDuotoneOpacityPrimary={
            leftSlotProps?.iconDuotoneOpacityPrimary || leftSlotDuotoneOpacityPrimary
          }
          leftSlotDuotoneOpacitySecondary={
            leftSlotProps?.iconDuotoneOpacitySecondary || leftSlotDuotoneOpacitySecondary
          }
          hasChildren={hasChildren}
        />
      )}

      {/* Contenido de texto */}
      {hasChildren && (
        <ButtonContent
          displayText={displayText}
          hasSlots={!!(effectiveLeftSlot || effectiveRightSlot)}
          textStyle={textStyle}
          textColor={textColor}
          textAlign={textAlign}
          measureRef={measureRef}
          textRef={textRef}
          measureSpanClassName={measureSpanClassName}
        />
      )}

      {/* Badge - después del contenido */}
      {badge?.show !== false && shouldShowBadgeValue && hasChildren && (
        <LegacyButtonBadge
          shouldShow={shouldShowBadgeValue}
          badgeColor={effectiveBadgeColor}
          badgeLabel={effectiveBadgeLabel}
          badgeTheme={effectiveBadgeTheme}
          badgeSize={effectiveBadgeSizeValue}
          badgeStroke={computedBadgeStroke}
          badgeIcon={effectiveBadgeIcon as LegacyIconName | undefined}
          badgeIconDuotonePrimary={badge?.iconDuotonePrimary || badgeIconDuotonePrimary}
          badgeIconDuotoneSecondary={badge?.iconDuotoneSecondary || badgeIconDuotoneSecondary}
          badgeIconDuotoneOpacityPrimary={
            badge?.iconDuotoneOpacityPrimary || badgeIconDuotoneOpacityPrimary
          }
          badgeIconDuotoneOpacitySecondary={
            badge?.iconDuotoneOpacitySecondary || badgeIconDuotoneOpacitySecondary
          }
          badgeLoading={effectiveBadgeLoading}
          isAbsolute={false}
        />
      )}

      {/* Slot derecho - después del badge */}
      {rightSlotProps?.show !== false && (
        <LegacyButtonSlotRight
          rightSlot={effectiveRightSlot}
          size={size}
          color={color}
          colorIcon={rightSlotProps?.colorIcon || colorIcon}
          className={className}
          disabled={disabled}
          strokeWidth={strokeWidth}
          onClickRightSlot={rightSlotProps?.onClick || onClickRightSlot}
          rightSlotDuotonePrimary={rightSlotProps?.iconDuotonePrimary || rightSlotDuotonePrimary}
          rightSlotDuotoneSecondary={
            rightSlotProps?.iconDuotoneSecondary || rightSlotDuotoneSecondary
          }
          rightSlotDuotoneOpacityPrimary={
            rightSlotProps?.iconDuotoneOpacityPrimary || rightSlotDuotoneOpacityPrimary
          }
          rightSlotDuotoneOpacitySecondary={
            rightSlotProps?.iconDuotoneOpacitySecondary || rightSlotDuotoneOpacitySecondary
          }
          hasChildren={hasChildren}
        />
      )}

      {iconProps?.show !== false && !hasChildren && effectiveIcon && (
        <LegacyButtonIcon
          icon={effectiveIcon}
          size={size}
          color={color}
          colorIcon={iconProps?.color || colorIcon}
          classNameIcon={iconProps?.className || classNameIcon}
          theme={theme}
          onClick={onClick}
          onClickIcon={iconProps?.onClick || onClickIcon}
          iconDuotonePrimary={
            iconProps?.colorDuotonePrimary || iconProps?.iconDuotonePrimary || iconDuotonePrimary
          }
          iconDuotoneSecondary={
            iconProps?.colorDuotoneSecondary ||
            iconProps?.iconDuotoneSecondary ||
            iconDuotoneSecondary
          }
          iconDuotoneOpacityPrimary={
            iconProps?.opacityDuotonePrimary ||
            iconProps?.iconDuotoneOpacityPrimary ||
            iconDuotoneOpacityPrimary
          }
          iconDuotoneOpacitySecondary={
            iconProps?.opacityDuotoneSecondary ||
            iconProps?.iconDuotoneOpacitySecondary ||
            iconDuotoneOpacitySecondary
          }
        />
      )}
    </>
  );

  // Renderizar badge absoluto para icon buttons
  const renderAbsoluteBadge = () => {
    if (!isIconButton || !shouldShowBadgeValue || badge?.show === false) {
      return null;
    }

    return (
      <LegacyButtonBadge
        shouldShow={shouldShowBadgeValue}
        badgeColor={effectiveBadgeColor}
        badgeLabel={effectiveBadgeLabel}
        badgeTheme={effectiveBadgeTheme}
        badgeSize={effectiveBadgeSizeValue}
        badgeStroke={computedBadgeStroke}
        badgeIcon={effectiveBadgeIcon as LegacyIconName | undefined}
        badgeIconDuotonePrimary={badge?.iconDuotonePrimary || badgeIconDuotonePrimary}
        badgeIconDuotoneSecondary={badge?.iconDuotoneSecondary || badgeIconDuotoneSecondary}
        badgeIconDuotoneOpacityPrimary={
          badge?.iconDuotoneOpacityPrimary || badgeIconDuotoneOpacityPrimary
        }
        badgeIconDuotoneOpacitySecondary={
          badge?.iconDuotoneOpacitySecondary || badgeIconDuotoneOpacitySecondary
        }
        badgeLoading={effectiveBadgeLoading}
        isAbsolute={true}
      />
    );
  };

  // Renderizar como link
  if (link) {
    return (
      <a
        ref={ref as React.RefObject<HTMLAnchorElement>}
        href={link}
        target={target}
        className={cn(buttonClasses, className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {renderContent()}
        {renderAbsoluteBadge()}
      </a>
    );
  }

  // Renderizar como div
  if (as === 'div') {
    return (
      <Tooltip
        label={effectiveTooltipLabel || ''}
        position={effectiveTooltipPosition}
        alignment={effectiveTooltipAlignment}
        absolute={effectiveTooltipAbsolute}
        delay={effectiveTooltipDelay}
        className={effectiveTooltipClassName}
        fullWidth={fullWidth}
      >
        <div
          ref={ref as React.RefObject<HTMLDivElement>}
          className={cn(buttonClasses, className)}
          role={clickable && !disabled ? 'button' : undefined}
          tabIndex={clickable && !disabled ? 0 : undefined}
          aria-disabled={disabled}
          aria-label={!hasChildren && icon ? effectiveTooltipLabel || 'Button' : undefined}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          {...props}
        >
          {renderContent()}
          {renderAbsoluteBadge()}
        </div>
      </Tooltip>
    );
  }

  // Renderizar como button (por defecto)
  return (
    <Tooltip
      label={effectiveTooltipLabel || ''}
      position={effectiveTooltipPosition}
      alignment={effectiveTooltipAlignment}
      delay={effectiveTooltipDelay}
      className={effectiveTooltipClassName}
      fullWidth={fullWidth}
    >
      <button
        ref={ref as React.RefObject<HTMLButtonElement>}
        className={cn(buttonClasses, className)}
        aria-label={!hasChildren && icon ? effectiveTooltipLabel || 'Button' : undefined}
        aria-disabled={disabled}
        {...buttonProps}
        {...props}
        onClick={handleClick}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {renderContent()}
        {renderAbsoluteBadge()}
      </button>
    </Tooltip>
  );
});

InnerLegacyButton.displayName = 'InnerLegacyButton';
